import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import SEOHead from '../components/SEOHead';
import Calculator from '../components/Calculator';
import Input from '../components/Input';
import { 
  Tractor, 
  DollarSign, 
  Calculator as CalcIcon, 
  Percent, 
  CreditCard,
  FileText,
  Info,
  TrendingDown,
  LineChart as LineChartIcon,
  AlertCircle
} from 'lucide-react';
import { 
  FarmFinanceInputs,
  FarmFinanceResult,
  FARM_VEHICLE_TYPES,
  SEASONAL_PERIODS,
  calculateFarmFinance,
  formatCurrency,
  formatPercentage,
  calculateEffectiveRate,
  checkInstantAssetWriteOffEligibility
} from '../utils/farmVehicleFinanceCalculator';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer, 
  BarChart, 
  Bar,
  PieChart,
  Pie,
  Cell
} from 'recharts';

// Color palette for charts
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

const FarmVehicleFinanceCalculator: React.FC = () => {
  // State for form inputs
  const [vehiclePrice, setVehiclePrice] = useState('75000');
  const [downPayment, setDownPayment] = useState('15000');
  const [farmEquipment, setFarmEquipment] = useState('10000');
  const [implementsAttachments, setImplementsAttachments] = useState('5000');
  const [loanTerm, setLoanTerm] = useState('60');
  const [interestRate, setInterestRate] = useState('5.9');
  const [balloonPayment, setBalloonPayment] = useState('0');
  const [applicationFee, setApplicationFee] = useState('495');
  const [annualFee, setAnnualFee] = useState('150');
  const [businessUsePercentage, setBusinessUsePercentage] = useState('100');
  const [seasonalPayments, setSeasonalPayments] = useState(false);
  const [primaryProducer, setPrimaryProducer] = useState(true);
  const [vehicleType, setVehicleType] = useState('tractor');
  
  // State for results
  const [result, setResult] = useState<FarmFinanceResult | null>(null);
  
  // Function to calculate loan
  const calculateLoan = () => {
    // Validate inputs and create inputs object
    const inputs: FarmFinanceInputs = {
      vehiclePrice: parseFloat(vehiclePrice) || 0,
      downPayment: parseFloat(downPayment) || 0,
      farmEquipment: parseFloat(farmEquipment) || 0,
      implementsAttachments: parseFloat(implementsAttachments) || 0,
      loanTerm: parseInt(loanTerm) || 60,
      interestRate: parseFloat(interestRate) || 0,
      balloonPayment: parseFloat(balloonPayment) || 0,
      applicationFee: parseFloat(applicationFee) || 0,
      annualFee: parseFloat(annualFee) || 0,
      businessUsePercentage: parseFloat(businessUsePercentage) || 100,
      seasonalPayments,
      primaryProducer
    };
    
    // Calculate results
    const financeResult = calculateFarmFinance(inputs);
    setResult(financeResult);
  };
  
  // Function to check instant asset write-off eligibility
  const isEligibleForInstantAssetWriteOff = (): boolean => {
    const totalAssetValue = 
      (parseFloat(vehiclePrice) || 0) + 
      (parseFloat(farmEquipment) || 0) + 
      (parseFloat(implementsAttachments) || 0);
    
    return checkInstantAssetWriteOffEligibility(totalAssetValue, primaryProducer);
  };
  
  // Function to get effective interest rate after tax
  const getEffectiveRate = (): string => {
    const rate = calculateEffectiveRate(
      parseFloat(interestRate) || 0,
      parseFloat(businessUsePercentage) || 100
    );
    
    return rate.toFixed(2);
  };
  
  // Chart data for depreciation
  const getDepreciationChartData = () => {
    if (!result) return [];
    
    return result.depreciationSchedule.map(entry => ({
      name: `Year ${entry.year}`,
      Depreciation: entry.depreciationAmount,
      'Tax Saving': entry.taxSaving,
      'Asset Value': entry.closingValue
    }));
  };
  
  // Chart data for payment breakdown
  const getPaymentBreakdownData = () => {
    if (!result) return [];
    
    return [
      { name: 'Principal', value: result.loanAmount },
      { name: 'Interest', value: result.totalInterest }
    ];
  };
  
  return (
    <>
      <SEOHead
        title="Farm Vehicle Finance Calculator | Agricultural Equipment Loan Calculator Australia"
        description="Calculate farm vehicle and equipment finance repayments with tax benefits for Australian farmers. Specialized for tractors, harvesters, irrigation systems and farm equipment."
        keywords="farm vehicle finance, agricultural equipment loan, tractor finance australia, farm machinery finance calculator, primary producer loan, seasonal payment calculator, farm equipment tax deduction calculator"
      />
      <Helmet>
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "FinancialProduct",
              "name": "Farm Vehicle Finance Calculator",
              "description": "Calculate farm vehicle and equipment finance repayments with tax benefits for Australian farmers.",
              "category": "LoanOrCredit",
              "feesAndCommissionsSpecification": "Free online calculator",
              "areaServed": {
                "@type": "Country",
                "name": "Australia"
              }
            }
          `}
        </script>
      </Helmet>
      
      <Calculator
        title="Farm Vehicle & Equipment Finance Calculator"
        description="Calculate repayments on agricultural vehicles and equipment with tax deductions, seasonal payment options, and instant asset write-off benefits."
        icon={Tractor}
        result={result ? `Monthly Repayment: ${formatCurrency(result.monthlyPayment)} | After Tax: ${formatCurrency(result.effectiveMonthlyCost)}` : undefined}
      >
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <Tractor className="h-5 w-5 text-blue-600 mr-2" />
              Farm Vehicle Details
            </h3>
            
            <div className="form-control w-full mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle Type</label>
              <select 
                className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200"
                value={vehicleType}
                onChange={(e) => setVehicleType(e.target.value)}
              >
                {FARM_VEHICLE_TYPES.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            
            <Input
              label="Vehicle Price"
              value={vehiclePrice}
              onChange={setVehiclePrice}
              unit="$"
              placeholder="e.g. 75000"
            />
            
            <Input
              label="Down Payment"
              value={downPayment}
              onChange={setDownPayment}
              unit="$"
              placeholder="e.g. 15000"
            />
            
            <Input
              label="Additional Farm Equipment"
              value={farmEquipment}
              onChange={setFarmEquipment}
              unit="$"
              placeholder="e.g. 10000"
            />
            
            <Input
              label="Implements & Attachments"
              value={implementsAttachments}
              onChange={setImplementsAttachments}
              unit="$"
              placeholder="e.g. 5000"
            />
            
            <div className="bg-blue-50 p-4 rounded-md mb-4">
              <div className="flex items-start">
                <Info className="h-5 w-5 text-blue-600 mr-2 mt-0.5" />
                <p className="text-sm text-blue-700">
                  Instant asset write-off is {isEligibleForInstantAssetWriteOff() ? 'available' : 'not available'} for this purchase.
                  {isEligibleForInstantAssetWriteOff() && ' You can claim the full asset cost as a tax deduction in the first year.'}
                </p>
              </div>
            </div>
          </div>
          
          <div>
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <CreditCard className="h-5 w-5 text-blue-600 mr-2" />
              Loan Details
            </h3>
            
            <Input
              label="Loan Term"
              value={loanTerm}
              onChange={setLoanTerm}
              unit="months"
              placeholder="e.g. 60"
            />
            
            <Input
              label="Interest Rate"
              value={interestRate}
              onChange={setInterestRate}
              unit="%"
              placeholder="e.g. 5.9"
            />
            
            <Input
              label="Balloon Payment"
              value={balloonPayment}
              onChange={setBalloonPayment}
              unit="$"
              placeholder="e.g. 0"
            />
            
            <h3 className="text-lg font-semibold mb-4 flex items-center mt-6">
              <DollarSign className="h-5 w-5 text-blue-600 mr-2" />
              Fees & Tax Details
            </h3>
            
            <Input
              label="Application Fee"
              value={applicationFee}
              onChange={setApplicationFee}
              unit="$"
              placeholder="e.g. 495"
            />
            
            <Input
              label="Annual Fee"
              value={annualFee}
              onChange={setAnnualFee}
              unit="$"
              placeholder="e.g. 150"
            />
            
            <Input
              label="Business Use Percentage"
              value={businessUsePercentage}
              onChange={setBusinessUsePercentage}
              unit="%"
              placeholder="e.g. 100"
            />
            
            <div className="mb-4">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={primaryProducer}
                  onChange={() => setPrimaryProducer(!primaryProducer)}
                  className="form-checkbox h-4 w-4 text-blue-600"
                />
                <span className="text-sm text-gray-700">Primary Producer (eligible for tax benefits)</span>
              </label>
            </div>
            
            <div className="mb-4">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={seasonalPayments}
                  onChange={() => setSeasonalPayments(!seasonalPayments)}
                  className="form-checkbox h-4 w-4 text-blue-600"
                />
                <span className="text-sm text-gray-700">Use Seasonal Payments (higher during harvest, lower in off-season)</span>
              </label>
            </div>
          </div>
        </div>
        
        <button
          onClick={calculateLoan}
          className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition mt-6 font-bold"
        >
          Calculate Farm Vehicle Finance
        </button>
        
        {/* Results Section */}
        {result && (
          <div className="mt-8 space-y-8">
            {/* Loan Summary */}
            <div className="p-6 bg-blue-50 rounded-lg border border-blue-100">
              <h3 className="text-xl font-semibold text-blue-900 mb-4">Finance Summary</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white p-4 rounded-md shadow-sm">
                  <p className="text-sm text-gray-600">Monthly Repayment</p>
                  <p className="text-xl font-bold text-blue-800">{formatCurrency(result.monthlyPayment)}</p>
                </div>
                
                <div className="bg-white p-4 rounded-md shadow-sm">
                  <p className="text-sm text-gray-600">Total Interest</p>
                  <p className="text-xl font-bold text-blue-800">{formatCurrency(result.totalInterest)}</p>
                </div>
                
                <div className="bg-white p-4 rounded-md shadow-sm">
                  <p className="text-sm text-gray-600">Total Finance Cost</p>
                  <p className="text-xl font-bold text-blue-800">{formatCurrency(result.totalCost)}</p>
                </div>
              </div>
            </div>
            
            {/* Tax Benefits Summary */}
            <div className="p-6 bg-green-50 rounded-lg border border-green-100">
              <h3 className="text-xl font-semibold text-green-900 mb-4">Tax Benefits Summary</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white p-4 rounded-md shadow-sm">
                  <p className="text-sm text-gray-600">Total Tax Savings</p>
                  <p className="text-xl font-bold text-green-800">{formatCurrency(result.estimatedTaxSavings)}</p>
                </div>
                
                <div className="bg-white p-4 rounded-md shadow-sm">
                  <p className="text-sm text-gray-600">Effective Monthly Cost</p>
                  <p className="text-xl font-bold text-green-800">{formatCurrency(result.effectiveMonthlyCost)}</p>
                </div>
                
                <div className="bg-white p-4 rounded-md shadow-sm">
                  <p className="text-sm text-gray-600">Effective Interest Rate</p>
                  <p className="text-xl font-bold text-green-800">{getEffectiveRate()}%</p>
                </div>
              </div>
              
              <div className="mt-4 bg-green-100 p-4 rounded-md">
                <div className="flex items-start">
                  <AlertCircle className="h-5 w-5 text-green-700 mr-2 mt-0.5" />
                  <p className="text-sm text-green-800">
                    {primaryProducer ? 'As a primary producer, you can claim business expenses as tax deductions.' : 'Consider registering as a primary producer to benefit from additional tax deductions.'}
                    {isEligibleForInstantAssetWriteOff() && ' Your purchase qualifies for instant asset write-off, allowing a full tax deduction in the first year.'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* General Information */}
        {!result && (
          <div className="mt-8 bg-gray-50 p-6 rounded-lg">
            <h3 className="text-xl font-semibold mb-4">About Farm Vehicle Finance</h3>
            <p className="text-gray-700 mb-4">
              Our Farm Vehicle Finance Calculator is specifically designed for Australian farmers and agricultural businesses.
              It helps calculate repayments on tractors, harvesters, farm utility vehicles, and other farm equipment while accounting for:
            </p>
            
            <ul className="list-disc pl-5 text-gray-700 space-y-2 mb-6">
              <li>Tax deductions based on business use percentage</li>
              <li>Instant asset write-off benefits for eligible purchases</li>
              <li>Seasonal payment options that align with farm income cycles</li>
              <li>Depreciation schedules and their tax implications</li>
              <li>Total cost of ownership including all fees and interest</li>
            </ul>
            
            <p className="text-gray-700">
              Enter your vehicle and finance details above to get a comprehensive breakdown of your farm equipment financing,
              including tax benefits and effective costs after tax savings.
            </p>
          </div>
        )}
      </Calculator>
    </>
  );
};

export default FarmVehicleFinanceCalculator; 